import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Wrench,
  AlertTriangle,
  Clock,
  ChevronRight,
  Package,
  MapPin
} from 'lucide-react';
import { componentService } from '../../services/componentService';

const ComponentsDueWidget = () => {
  const [components, setComponents] = useState([]);
  const [summary, setSummary] = useState({ overdue: 0, dueSoon: 0, total: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [dueData, summaryData] = await Promise.all([
        componentService.getDueComponents(),
        componentService.getAlertSummary()
      ]);
      setComponents(dueData || []);
      setSummary(summaryData || { overdue: 0, dueSoon: 0, total: 0 });
    } catch (error) {
      console.error('Error loading component data:', error);
      setComponents([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-48 mb-4"></div>
          <div className="space-y-3">
            <div className="h-12 bg-gray-100 rounded"></div>
            <div className="h-12 bg-gray-100 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (summary.total === 0) {
    return null; // Don't show widget if no due components
  }

  const overdueComponents = components.filter(c => c.isOverdue);
  const dueSoonComponents = components.filter(c => c.isDueSoon && !c.isOverdue);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Wrench className="w-5 h-5 text-orange-500" />
          <h3 className="font-semibold text-gray-900">Components Due for Replacement</h3>
        </div>
        <Link
          to="/portal/cmms/assets"
          className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
        >
          View All
          <ChevronRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="p-4 space-y-4">
        {/* Overdue Section */}
        {overdueComponents.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-red-500" />
              <span className="text-sm font-medium text-red-600">
                OVERDUE ({overdueComponents.length})
              </span>
            </div>
            <div className="space-y-2">
              {overdueComponents.slice(0, 3).map(component => (
                <ComponentItem key={component.id} component={component} isOverdue />
              ))}
              {overdueComponents.length > 3 && (
                <p className="text-sm text-gray-500 pl-2">
                  +{overdueComponents.length - 3} more overdue
                </p>
              )}
            </div>
          </div>
        )}

        {/* Due Soon Section */}
        {dueSoonComponents.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-yellow-500" />
              <span className="text-sm font-medium text-yellow-600">
                DUE SOON ({dueSoonComponents.length})
              </span>
            </div>
            <div className="space-y-2">
              {dueSoonComponents.slice(0, 3).map(component => (
                <ComponentItem key={component.id} component={component} />
              ))}
              {dueSoonComponents.length > 3 && (
                <p className="text-sm text-gray-500 pl-2">
                  +{dueSoonComponents.length - 3} more due soon
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ComponentItem = ({ component, isOverdue }) => {
  const daysText = isOverdue
    ? `${Math.abs(component.daysUntilDue)} days overdue`
    : `${component.daysUntilDue} days`;

  return (
    <Link
      to={`/portal/cmms/assets/${component.assetId}`}
      className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 group"
    >
      <div className="flex items-center gap-3">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
          isOverdue ? 'bg-red-100' : 'bg-yellow-100'
        }`}>
          <Package className={`w-4 h-4 ${isOverdue ? 'text-red-600' : 'text-yellow-600'}`} />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900 text-sm">{component.name}</span>
            <span className="text-gray-400">-</span>
            <span className="text-sm text-gray-600">{component.assetName}</span>
          </div>
          {component.branchName && (
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <MapPin className="w-3 h-3" />
              {component.branchName}
              {component.locationName && ` > ${component.locationName}`}
            </div>
          )}
        </div>
      </div>
      <span className={`text-sm font-medium ${
        isOverdue ? 'text-red-600' : 'text-yellow-600'
      }`}>
        {daysText}
      </span>
    </Link>
  );
};

export default ComponentsDueWidget;
